import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, addDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Key, Plus, Trash2, RefreshCw, Copy } from 'lucide-react';
import Loading from './Loading';
import { useToast } from '../../contexts/ToastContext';
import { createPortal } from 'react-dom';

// Confirmation Modal Component
const ConfirmationModal = ({ show, keyId, onConfirm, onCancel }) => {
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  if (!show) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-[90%] max-w-md shadow-lg">
        <h4 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
          Delete API Key
        </h4>
        
        <p className="text-slate-600 dark:text-slate-300 mb-6 text-sm leading-relaxed">
          Are you sure you want to delete this API key? This action cannot be undone.
        </p>
        
        <div className="flex justify-end gap-3">
          <button 
            onClick={onCancel}
            className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-md text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          
          <button 
            onClick={() => onConfirm(keyId)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ApiKeyManagement = () => {
  const { currentUser, currentOrg } = useAuth();
  const { showSuccess } = useToast();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingKey, setCreatingKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  // Fetch all API keys
  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const apiKeysRef = collection(db, 'apiKeys');
      const q = query(apiKeysRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const keys = [];
      snapshot.forEach((doc) => {
        keys.push({
          id: doc.id,
          ...doc.data(),
          // Format dates for display
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          lastUsed: doc.data().lastUsed?.toDate?.() || null
        });
      });
      
      setApiKeys(keys);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to load API keys: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load API keys on component mount
  useEffect(() => {
    fetchApiKeys();
  }, []);

  // Helper function to hash the API key using SHA-256
  const hashApiKey = async (key) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(key);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Create a new API key
  const createNewApiKey = async () => {
    setCreatingKey(true);
    setError(null);
    try {
      // Generate a random API key
      const key = generateApiKey();
      // Hash the API key
      const keyHash = await hashApiKey(key);
      // Store only the hash and preview in Firestore
      const apiKeysRef = collection(db, 'apiKeys');
      await addDoc(apiKeysRef, {
        keyHash: keyHash,
        keyPreview: key.substring(0, 4),
        createdAt: serverTimestamp(),
        lastUsed: null,
        enabled: true,
        usageCount: 0,
        dailyUsage: {},
        userId: currentUser.uid,
        userEmail: currentUser.email,
        orgId: currentOrg.id
      });
      setNewApiKey(key);
      setShowNewKey(true);
      fetchApiKeys();
    } catch (err) {
      console.error('Error creating API key:', err);
      setError('Failed to create API key: ' + err.message);
    } finally {
      setCreatingKey(false);
    }
  };

  // Show delete confirmation modal
  const confirmDeleteApiKey = (keyId) => {
    setKeyToDelete(keyId);
    setShowDeleteConfirmation(true);
  };

  // Delete an API key after confirmation
  const deleteApiKey = async (keyId) => {
    try {
      await deleteDoc(doc(db, 'apiKeys', keyId));
      setApiKeys(apiKeys.filter(key => key.id !== keyId));
      showSuccess('API key deleted successfully');
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError('Failed to delete API key: ' + err.message);
    } finally {
      setShowDeleteConfirmation(false);
      setKeyToDelete(null);
    }
  };

  // Cancel deletion
  const cancelDeleteApiKey = () => {
    setShowDeleteConfirmation(false);
    setKeyToDelete(null);
  };

  // Helper function to generate a random API key
  const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const keyLength = 32;
    let key = '';
    
    for (let i = 0; i < keyLength; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return key;
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  // Copy API key to clipboard
  const copyApiKey = (key) => {
    navigator.clipboard.writeText(key)
      .then(() => {
        showSuccess('API key copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy API key:', err);
      });
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">API Key Management</h2>
      
      {/* Error display */}
      {error && (
        <div className="mb-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700">
          <p>{error}</p>
        </div>
      )}
      
      {/* New API key form */}
      <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
        <h3 className="text-lg font-medium mb-3">Create New API Key</h3>
        <p className="mb-4 text-slate-600 dark:text-slate-400">
          Click the button below to generate a new API key associated with your account.
        </p>
        
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
          onClick={createNewApiKey}
          disabled={creatingKey}
        >
          {creatingKey ? (
            <>
              <RefreshCw size={16} className="mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Plus size={16} className="mr-2" />
              Create API Key
            </>
          )}
        </button>
      </div>
      
      {/* Newly created API key display */}
      {showNewKey && newApiKey && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700">
          <div className="flex justify-between">
            <div className="flex-grow">
              <p className="font-bold mb-2">New API Key Created:</p>
              <div className="flex items-center mb-2">
                <button 
                  className="mr-2 p-2 bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-200 rounded hover:bg-green-300 dark:hover:bg-green-600 transition-colors"
                  onClick={() => copyApiKey(newApiKey)}
                  title="Copy API Key"
                >
                  <Copy size={16} />
                </button>
                <code className="bg-white dark:bg-slate-800 p-2 rounded overflow-x-auto flex-grow">
                  {newApiKey}
                </code>
              </div>
              <p className="text-sm text-green-600 dark:text-green-400">
                Important: Copy this key now. For security reasons, it won't be fully displayed again.
              </p>
            </div>
            <button
              className="text-green-800 dark:text-green-200 hover:text-green-600 ml-2"
              onClick={() => setShowNewKey(false)}
            >
              &times;
            </button>
          </div>
        </div>
      )}
      
      {/* API keys list */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-medium">API Keys</h3>
          <button
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
            onClick={fetchApiKeys}
          >
            <RefreshCw size={16} className="mr-1" />
            Refresh
          </button>
        </div>
        
        {loading ? (
          <Loading message="Loading API keys..." />
        ) : (
          <div className="overflow-x-auto">
            {apiKeys.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 py-4">No API keys found. Create one to get started.</p>
            ) : (
              <table className="min-w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-700">
                  <tr>
                    <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Key
                    </th>
                    <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Last Used
                    </th>
                    <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Usage Count
                    </th>
                    <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => (
                    <tr key={key.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                      <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-sm font-mono">
                        {key.keyPreview ? `${key.keyPreview}...` : 'N/A'}
                      </td>
                      <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-sm">
                        {key.userEmail || 'Unknown'}
                      </td>
                      <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-sm">
                        {formatDate(key.createdAt)}
                      </td>
                      <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-sm">
                        {formatDate(key.lastUsed)}
                      </td>
                      <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-sm">
                        {key.usageCount || 0}
                      </td>
                      <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${key.enabled ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>
                          {key.enabled ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600">
                        <button
                          onClick={() => confirmDeleteApiKey(key.id)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete API key"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Confirmation modal for deleting API key */}
      <ConfirmationModal
        show={showDeleteConfirmation}
        keyId={keyToDelete}
        onConfirm={deleteApiKey}
        onCancel={cancelDeleteApiKey}
      />
    </div>
  );
};

export default ApiKeyManagement; 